import { useState } from 'react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { X, FileText, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { StandardDialog } from '@/components/common/dialogs/StandardDialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Upload } from '@/types'

interface DataPreviewProps {
  upload: Upload | null
  open: boolean
  onClose: () => void
}

const ITEMS_PER_PAGE = 20

export function DataPreview({ upload, open, onClose }: DataPreviewProps) {
  const [currentPage, setCurrentPage] = useState(1)

  if (!upload) return null

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const currentData = upload.data.slice(startIndex, endIndex)
  const totalPages = Math.ceil(upload.data.length / ITEMS_PER_PAGE)

  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 B'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  const getStatusBadge = (status: Upload['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Laddar upp</Badge>
      case 'uploaded':
        return <Badge variant="default">Uppladdad</Badge>
      case 'processed':
        return <Badge variant="default">Bearbetad</Badge>
      case 'failed':
        return <Badge variant="destructive">Misslyckades</Badge>
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  const getPlatformName = (platform: string) => {
    switch (platform) {
      case 'bolt':
        return 'Bolt'
      case 'uber':
        return 'Uber'
      default:
        return platform.charAt(0).toUpperCase() + platform.slice(1)
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const resetPage = () => {
    setCurrentPage(1)
  }

  return (
    <StandardDialog
      open={open}
      onOpenChange={onClose}
      size="2xl"
      title={
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5" />
          {upload.fileName}
        </div>
      }
      scrollable={false}
    >
      <div className="space-y-4 h-full flex flex-col">
          {/* File metadata */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                <div>
                  <p className="font-medium text-muted-foreground">Uppladdad</p>
                  <p className="break-words">{format(upload.uploadedAt, 'PPp', { locale: sv })}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Storlek</p>
                  <p>{formatFileSize(upload.fileSize)}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Rader</p>
                  <p>{upload.rowCount.toLocaleString('sv-SE')}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Kolumner</p>
                  <p>{upload.headers.length}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Plattform</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{getPlatformName(upload.platform)}</span>
                    {getStatusBadge(upload.status)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        {/* Data table */}
        <div className="flex-1 min-h-0 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">
              Data ({startIndex + 1}-{Math.min(endIndex, upload.data.length)} av {upload.data.length})
            </h3>
            
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Sida {currentPage} av {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <div className="border rounded-md overflow-auto max-h-96">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  {upload.headers.map((header, index) => (
                    <TableHead key={index} className="min-w-32">
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentData.map((row, rowIndex) => (
                  <TableRow key={startIndex + rowIndex}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {startIndex + rowIndex + 1}
                    </TableCell>
                    {upload.headers.map((header, cellIndex) => (
                      <TableCell key={cellIndex} className="max-w-48">
                        <div className="truncate" title={String(row[header] || '')}>
                          {String(row[header] || '')}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </StandardDialog>
  )
}